import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import {
  Search,
  X,
  Plus,
  Layers,
  Tag,
  Clock,
  CalendarDays,
  CheckCircle2,
  Pencil,
  Trash2,
  AlertTriangle,
  Film,
  ShieldCheck,
} from 'lucide-react';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { workFlexibilityRequest, workFlexibilityResponse } from '../Types/AdminType';
import { createWorkFlexibility, deleteWorkFlexibility, updateWorkFlexibility } from '../Services/AdminService';

interface Recruiter {
  id: string;
  workFlexibility: string;
}

interface Props {
  data: workFlexibilityResponse[];
  onEditSubmit: (data: Recruiter) => void;
  onDeleteConfirm: (email: string) => void;
  onRegisterSubmit: (data: Partial<Recruiter>) => void;
  menuName: string;
}

const sameDay = (value: string, target: Date) => {
  const d = new Date(value);
  return d.toDateString() === target.toDateString();
};

const sameMonth = (value: string, target: Date) => {
  const d = new Date(value);
  return d.getMonth() === target.getMonth() && d.getFullYear() === target.getFullYear();
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const StatCard = ({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent: string;
}) => (
  <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
    <div className="flex items-center justify-between">
      <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${accent}`}>{icon}</span>
      <span className="text-2xl font-black text-slate-100">{value}</span>
    </div>
    <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
  </div>
);

const WorkFlexibilityComponent = ({ data, onEditSubmit, onDeleteConfirm, onRegisterSubmit, menuName }: Props) => {
  const [selectedRecruiter, setSelectedRecruiter] = useState<Recruiter | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [search, setSearch] = useState('');

  const [adminData, setAdminData] = useState<workFlexibilityRequest | null>({
    workFlexibility: '',
  });
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const { values, handleInputChange } = useFormController<workFlexibilityRequest>({
    workFlexibility: '',
  });

  const [newRecruiter, setNewRecruiter] = useState<Partial<Recruiter>>({
    workFlexibility: '',
  });

  const filteredData = search.trim()
    ? data.filter((recruiter) =>
        recruiter.workFlexibility.toLowerCase().includes(search.trim().toLowerCase())
      )
    : data;

  const now = new Date();
  const stats = {
    total: data.length,
    today: data.filter((r) => sameDay(r.createdAt, now)).length,
    week: data.filter((r) => now.getTime() - new Date(r.createdAt).getTime() <= 7 * 24 * 60 * 60 * 1000).length,
    month: data.filter((r) => sameMonth(r.createdAt, now)).length,
  };

  const handleEditClick = (recruiter: any) => {
    setSelectedRecruiter(recruiter as Recruiter);
    setIsEditOpen(true);
  };

  const handleDeleteClick = (recruiter: any) => {
    setSelectedRecruiter(recruiter as Recruiter);
    setIsDeleteOpen(true);
  };

  const handleCreateSubmit = async () => {
    setAlert(null);
    const response = await createWorkFlexibility(values);

    if (response.success) {
      setAdminData(values);
      setAlert({
        variant: 'success',
        title: 'Success...!',
        message: response.data.workFlexibility + ' has been added successfully.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } else {
      setAlert({
        variant: 'error',
        title: 'Registration Error',
        message: response.message,
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    }
  };

  const handleEditSubmit = async () => {
    setAlert(null);

    const response = await updateWorkFlexibility(values, selectedRecruiter?.id || '');

    if (response.success) {
      setAdminData(values);
      setAlert({
        variant: 'success',
        title: 'Success...!',
        message: response.data.workFlexibility + ' has been updated successfully.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } else {
      setAlert({
        variant: 'error',
        title: 'Registration Error',
        message: response.message,
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    }
  };

  const handleDeleteSubmit = async () => {
    setAlert(null);
    const response = await deleteWorkFlexibility(selectedRecruiter?.id || '');

    if (response.success) {
      setAdminData(values);
      setAlert({
        variant: 'success',
        title: 'Success...!',
        message: selectedRecruiter?.workFlexibility + ' has been deleted successfully.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } else {
      setAlert({
        variant: 'error',
        title: 'Delete Error',
        message: response.message,
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} />}

      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <Layers size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">{menuName}</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Manage and monitor {menuName.toLowerCase()} categories
            </p>
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => setIsRegisterOpen(true)}
          className="flex items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400"
        >
          <Plus size={15} /> Add {menuName}
        </motion.button>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label={`Total ${menuName}s`}
          value={stats.total}
          icon={<Layers size={16} />}
          accent="border-cyan-500/30 bg-cyan-500/10 text-cyan-400"
        />
        <StatCard
          label="Added Today"
          value={stats.today}
          icon={<Clock size={16} />}
          accent="border-sky-500/30 bg-sky-500/10 text-sky-400"
        />
        <StatCard
          label="Added This Week"
          value={stats.week}
          icon={<CalendarDays size={16} />}
          accent="border-amber-500/30 bg-amber-500/10 text-amber-400"
        />
        <StatCard
          label="Added This Month"
          value={stats.month}
          icon={<CheckCircle2 size={16} />}
          accent="border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
        />
      </div>

      {/* Filters */}
      <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
          <Search size={14} /> Search
        </div>
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${menuName.toLowerCase()}...`}
            className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-9 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
            >
              <X size={15} />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
        <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-slate-900/70 px-5 py-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-800 bg-slate-950 text-cyan-400">
                <Tag size={18} />
              </span>
              <div>
                <h3 className="text-sm font-black tracking-tight text-slate-100 md:text-base">
                  {menuName} History
                </h3>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  {filteredData.length} item{filteredData.length === 1 ? '' : 's'}
                </p>
              </div>
            </div>
          </div>

          {filteredData.length === 0 ? (
            <div className="border border-dashed border-slate-700 bg-slate-900/30 py-20 text-center">
              <ShieldCheck className="mx-auto mb-2 h-8 w-8 text-slate-600" />
              <p className="text-xs font-black uppercase tracking-widest text-slate-400">No {menuName.toLowerCase()} found</p>
              {search && (
                <button onClick={() => setSearch('')} className="mt-3 text-xs font-bold text-cyan-400 hover:underline">
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-slate-800 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <th className="px-5 py-3">Name</th>
                    <th className="px-5 py-3">Created</th>
                    <th className="px-5 py-3">Updated</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((recruiter, idx) => (
                    <motion.tr
                      key={recruiter.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.02 }}
                      className="border-b border-slate-800/60 transition last:border-0 hover:bg-slate-800/20"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-sm font-black uppercase text-cyan-400">
                            {recruiter.workFlexibility?.[0] || '?'}
                          </span>
                          <p className="text-sm font-bold text-slate-100">{recruiter.workFlexibility || '—'}</p>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                          <CalendarDays size={12} className="text-slate-500" />
                          {formatDate(recruiter.createdAt)}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                          <Film size={12} className="text-slate-500" />
                          {formatDate(recruiter.updatedAt)}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEditClick(recruiter)}
                            title="Edit"
                            className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40"
                          >
                            <Pencil size={12} /> Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(recruiter)}
                            title="Delete"
                            className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-rose-400 transition hover:border-rose-500/40"
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>

      {/* --- REGISTER MODAL --- */}
      <Transition show={isRegisterOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={() => setIsRegisterOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-xl rounded-2xl border border-slate-800 bg-slate-900 p-8 shadow-2xl">
                  <div className="mb-6 flex items-start justify-between">
                    <div>
                      <Dialog.Title className="text-xl font-black tracking-tight text-slate-100">
                        Create New {menuName}
                      </Dialog.Title>
                      <p className="mt-1 text-xs font-semibold text-slate-400">
                        Add a new {menuName.toLowerCase()} category to your Job Portal
                      </p>
                    </div>
                    <button
                      onClick={() => setIsRegisterOpen(false)}
                      className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-800"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      Work Flexibility
                    </label>
                    <input
                      type="text"
                      name="workFlexibility"
                      placeholder="Enter the work flexibility option (e.g., Remote, On-site, Hybrid)"
                      value={values.workFlexibility}
                      onChange={handleInputChange('workFlexibility')}
                      className="w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
                    />
                  </div>

                  <button
                    onClick={() => {
                      handleCreateSubmit();
                      onRegisterSubmit(newRecruiter);
                      setIsRegisterOpen(false);
                    }}
                    className="mt-6 w-full rounded-xl bg-cyan-500 py-3 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400"
                  >
                    Create {menuName}
                  </button>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* --- EDIT MODAL --- */}
      <Transition show={isEditOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={() => setIsEditOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 p-8 shadow-2xl">
                  <div className="mb-6 flex items-start justify-between">
                    <div>
                      <Dialog.Title className="text-xl font-black tracking-tight text-slate-100">
                        Edit {selectedRecruiter?.workFlexibility} {menuName}
                      </Dialog.Title>
                      <p className="mt-1 text-xs font-semibold text-slate-400">
                        Update the {menuName.toLowerCase()} category details
                      </p>
                    </div>
                    <button
                      onClick={() => setIsEditOpen(false)}
                      className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-800"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      Work Flexibility
                    </label>
                    <input
                      type="text"
                      defaultValue={selectedRecruiter?.workFlexibility}
                      onChange={handleInputChange('workFlexibility')}
                      className="w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
                    />
                  </div>

                  <button
                    onClick={() => {
                      handleEditSubmit();
                      onEditSubmit(selectedRecruiter!);
                      setIsEditOpen(false);
                    }}
                    className="mt-6 w-full rounded-xl bg-cyan-500 py-3 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400"
                  >
                    Update {menuName}
                  </button>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* --- DELETE MODAL --- */}
      <Transition show={isDeleteOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={() => setIsDeleteOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-96"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-96"
              >
                <Dialog.Panel className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl">
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400">
                      <AlertTriangle size={20} />
                    </span>
                    <div>
                      <Dialog.Title className="text-base font-black tracking-tight text-slate-100">
                        Delete {menuName}?
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-slate-400">
                        This will permanently remove{' '}
                        <span className="font-bold text-slate-200">"{selectedRecruiter?.workFlexibility}"</span>. This
                        action cannot be undone.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 flex items-center justify-end gap-3">
                    <button
                      onClick={() => setIsDeleteOpen(false)}
                      className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        handleDeleteSubmit();
                        if (selectedRecruiter) onDeleteConfirm(selectedRecruiter.workFlexibility);
                        setIsDeleteOpen(false);
                      }}
                      className="flex items-center gap-2 rounded-xl bg-rose-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-rose-400"
                    >
                      <Trash2 size={15} /> Delete
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default WorkFlexibilityComponent;